import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getWeekStart } from '../lib/utils';

const TRIAL_DAYS = 7;
const TRIAL_DAILY_MESSAGES = 10;
// TEMP: payment/trial-expiry gate disabled — flip to true to re-enable
const PAYWALL_ENABLED = false;

export function useSubscription(userId, user, profile, onProfileUpdate) {
  const [checkinDone, setCheckinDone] = useState(null);

  useEffect(() => {
    if (!userId || !profile) return;
    if (profile.subscription_type === 'beta') {
      checkBetaCheckin();
    } else {
      setCheckinDone(true);
    }
  }, [userId, profile?.subscription_type]); // eslint-disable-line react-hooks/exhaustive-deps

  async function checkBetaCheckin() {
    const weekStart = getWeekStart(new Date());
    const { data } = await supabase
      .from('beta_checkins')
      .select('id')
      .eq('user_id', userId)
      .eq('week_start', weekStart)
      .maybeSingle();
    setCheckinDone(!!data);
  }

  const today = new Date().toISOString().split('T')[0];
  const userCreatedAt = user?.created_at ? new Date(user.created_at) : new Date();
  const trialExpiry = new Date(userCreatedAt.getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
  const trialActive = !PAYWALL_ENABLED || new Date() < trialExpiry;
  const trialDaysLeft = Math.max(0, Math.ceil((trialExpiry - new Date()) / (1000 * 60 * 60 * 24)));

  const dailyUsed = profile?.daily_messages_date === today
    ? (profile?.daily_messages_used || 0)
    : 0;
  const messagesLeft = Math.max(0, TRIAL_DAILY_MESSAGES - dailyUsed);

  const subType = profile?.subscription_type || 'free';
  const subExpiry = profile?.subscription_expires_at;
  const premiumActive = subType === 'premium' && subExpiry && new Date(subExpiry) > new Date();

  let accessLevel;
  if (premiumActive) {
    accessLevel = 'premium';
  } else if (subType === 'beta') {
    accessLevel = checkinDone === null ? 'loading' : checkinDone ? 'beta' : 'beta_needs_checkin';
  } else if (trialActive) {
    accessLevel = 'trial';
  } else {
    accessLevel = 'expired';
  }

  const canChat = ['premium', 'beta'].includes(accessLevel) ||
    (accessLevel === 'trial' && messagesLeft > 0);

  async function recordMessage() {
    if (!['free', 'trial'].includes(subType) || !trialActive) return;
    const newCount = profile?.daily_messages_date === today ? dailyUsed + 1 : 1;
    await supabase.from('profiles').update({
      daily_messages_used: newCount,
      daily_messages_date: today,
    }).eq('id', userId);
    if (onProfileUpdate) onProfileUpdate();
  }

  async function submitCheckin(data) {
    const weekStart = getWeekStart(new Date());
    const { error } = await supabase.from('beta_checkins').upsert({
      user_id: userId,
      week_start: weekStart,
      nps: data.nps,
      treatment_utility: data.treatmentUtility,
      feature_scores: data.featureScores,
      suggestion: data.suggestion || '',
    }, { onConflict: 'user_id,week_start' });
    if (!error) setCheckinDone(true);
    return !error;
  }

  return {
    accessLevel,
    trialDaysLeft,
    messagesLeft,
    canChat,
    recordMessage,
    submitCheckin,
    TRIAL_DAILY_MESSAGES,
  };
}
